import {
  DailyRegistrationOutput,
  DonorStatus,
  PeriodRegistrationOutput,
  useGetDonorPipelineStatsQuery,
} from 'gql/types';
import { useMemo } from 'react';

import { DateRangeType } from '../types/phlebotomist-dashboard';

export interface IDonorPipelineStats {
  activeDonorCount: number;
  pendingDonorCount: number;
  approvedDonorCount: number;
  registrationsByPeriod: PeriodRegistrationOutput[];
  lastWeekDaily: DailyRegistrationOutput[];
}

export interface IUseDonorPipelineStatsProps {
  regionIds?: string[];
  officeIds?: string[];
  locationIds?: string[];
  activeDonorStatuses?: DonorStatus[];
  pendingDonorStatuses?: DonorStatus[];
  approvedDonorStatuses?: DonorStatus[];
  skip?: boolean;
}

export interface IUseDonorPipelineStatsResult {
  stats: IDonorPipelineStats | null;
  loading: boolean;
  error?: Error;
  /** Get registration count for a specific date range type */
  getRegistrationCount: (dateRange: DateRangeType) => number;
  /** Get registration data for a specific date range type */
  getRegistrationData: (
    dateRange: DateRangeType
  ) => PeriodRegistrationOutput | null;
  /**
   * Get "yesterday" donors count based on day of week:
   * - Tuesday-Friday: previous day's count
   * - Monday: Friday + Saturday + Sunday (weekend total)
   */
  getYesterdayDonors: () => number;
  /**
   * Get today's donors count
   */
  getTodayDonors: () => number;
  /**
   * Get the label for "yesterday" based on day of week
   * - Monday: "Weekend" (Fri-Sun)
   * - Tuesday-Friday: "Yesterday"
   */
  getYesterdayLabel: () => string;
}

/**
 * Hook to fetch donor pipeline statistics including:
 * - Current active donor count (based on activeDonorStatuses)
 * - New donor registrations for all 20 time periods
 * - Last 7 days daily breakdown with ISO weekday
 */
export function useDonorPipelineStats(
  props: IUseDonorPipelineStatsProps = {}
): IUseDonorPipelineStatsResult {
  const {
    regionIds,
    officeIds,
    locationIds,
    activeDonorStatuses,
    pendingDonorStatuses,
    approvedDonorStatuses,
  } = props;

  const { data, loading, error } = useGetDonorPipelineStatsQuery({
    skip: props.skip,
    variables: {
      input: {
        regionIds: regionIds && regionIds.length > 0 ? regionIds : undefined,
        officeIds: officeIds && officeIds.length > 0 ? officeIds : undefined,
        locationIds:
          locationIds && locationIds.length > 0 ? locationIds : undefined,
        activeDonorStatuses:
          activeDonorStatuses && activeDonorStatuses.length > 0
            ? activeDonorStatuses
            : undefined,
        pendingDonorStatuses:
          pendingDonorStatuses && pendingDonorStatuses.length > 0
            ? pendingDonorStatuses
            : undefined,
        approvedDonorStatuses:
          approvedDonorStatuses && approvedDonorStatuses.length > 0
            ? approvedDonorStatuses
            : undefined,
      },
    },
  });

  const stats: IDonorPipelineStats | null = useMemo(() => {
    if (!data?.getDonorPipelineStats) return null;

    return {
      activeDonorCount: data.getDonorPipelineStats.activeDonorCount,
      pendingDonorCount: data.getDonorPipelineStats.pendingDonorCount,
      approvedDonorCount: data.getDonorPipelineStats.approvedDonorCount,
      registrationsByPeriod: data.getDonorPipelineStats.registrationsByPeriod,
      lastWeekDaily: data.getDonorPipelineStats.lastWeekDaily,
    };
  }, [data]);

  // Helper to get registration count for a specific date range
  const getRegistrationCount = useMemo(() => {
    return (dateRange: DateRangeType): number => {
      if (!stats?.registrationsByPeriod) return 0;
      const period = stats.registrationsByPeriod.find(
        (p) => p.period === dateRange
      );
      return period?.count ?? 0;
    };
  }, [stats]);

  // Helper to get full registration data for a specific date range
  const getRegistrationData = useMemo(() => {
    return (dateRange: DateRangeType): PeriodRegistrationOutput | null => {
      if (!stats?.registrationsByPeriod) return null;
      return (
        stats.registrationsByPeriod.find((p) => p.period === dateRange) ?? null
      );
    };
  }, [stats]);

  // Helper to get daily count by ISO weekday (1=Monday, 7=Sunday)
  // Uses the isoWeekday field from the backend data
  const getDailyCountByWeekday = (isoWeekday: number): number => {
    if (!stats?.lastWeekDaily) return 0;
    const day = stats.lastWeekDaily.find((d) => d.isoWeekday === isoWeekday);
    return day?.count ?? 0;
  };

  // Get "yesterday" donors based on day of week
  // Monday: show Friday + Saturday + Sunday = weekend total
  // Tuesday-Friday: show previous day
  // Saturday-Sunday: show Friday
  const getYesterdayDonors = useMemo(() => {
    return (): number => {
      const today = new Date().getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
      // Convert JS day (0=Sun) to ISO weekday (1=Mon, 7=Sun)
      const isoToday = today === 0 ? 7 : today;

      if (isoToday === 1) {
        // Monday: sum of Friday (5) + Saturday (6) + Sunday (7)
        return (
          getDailyCountByWeekday(5) +
          getDailyCountByWeekday(6) +
          getDailyCountByWeekday(7)
        );
      } else if (isoToday >= 2 && isoToday <= 5) {
        // Tuesday-Friday: previous day
        return getDailyCountByWeekday(isoToday - 1);
      } else if (isoToday === 6) {
        // Saturday: show Friday (5)
        return getDailyCountByWeekday(5);
      } else {
        // Sunday (7): show Friday (5)
        return getDailyCountByWeekday(5);
      }
    };
  }, [stats]);

  // Get today's donors count
  const getTodayDonors = useMemo(() => {
    return (): number => {
      const today = new Date().getDay();
      const isoToday = today === 0 ? 7 : today;
      return getDailyCountByWeekday(isoToday);
    };
  }, [stats]);

  // Get the label for "yesterday" based on day of week
  const getYesterdayLabel = useMemo(() => {
    return (): string => {
      const today = new Date().getDay();
      const isoToday = today === 0 ? 7 : today;

      if (isoToday === 1) {
        // Monday: showing Fri+Sat+Sun
        return 'Weekend';
      } else if (isoToday === 7) {
        // Sunday: showing Friday
        return 'Friday';
      }
      // Tuesday-Saturday: showing previous day
      return 'Yesterday';
    };
  }, []);

  return {
    stats,
    loading,
    error: error as Error | undefined,
    getRegistrationCount,
    getRegistrationData,
    getYesterdayDonors,
    getTodayDonors,
    getYesterdayLabel,
  };
}
